import type { OnboardingDataPoint } from "@/data/onboardingData";
import { JurisdictionBadge } from "./JurisdictionBadge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, FileText } from "lucide-react";
import { useState } from "react";

interface VisualDiagramProps {
  diagramLabel: string;
  dataPoints: OnboardingDataPoint[];
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
  readOnly?: boolean;
  imageSrc?: string;
}

export function VisualDiagram({ diagramLabel, dataPoints, selectedKeys, onToggle, readOnly = false, imageSrc }: VisualDiagramProps) {
  const visualPoints = dataPoints.filter(dp => dp.typical && dp.visualPosition);

  return (
    <div className="relative w-full h-full min-h-[400px] rounded-xl border bg-muted/30 overflow-hidden">
      {visualPoints.length > 0 && (
        <div className="absolute top-2 left-2 z-10 rounded-md bg-background/80 backdrop-blur px-2 py-0.5 text-[11px] text-muted-foreground border">
          Tip: hover any pin to see the rule
        </div>
      )}
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={diagramLabel}
          className="absolute inset-0 w-full h-full object-contain opacity-90 pointer-events-none select-none mix-blend-multiply"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center space-y-2 opacity-30">
            <div className="text-6xl">🏠</div>
            <p className="text-sm font-medium text-muted-foreground">{diagramLabel}</p>
          </div>
        </div>
      )}

      {visualPoints.map(dp => (
        <DiagramPin
          key={dp.key}
          dp={dp}
          selected={selectedKeys.has(dp.key)}
          onToggle={onToggle}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

function DiagramPin({
  dp,
  selected,
  onToggle,
  readOnly,
}: {
  dp: OnboardingDataPoint;
  selected: boolean;
  onToggle: (key: string) => void;
  readOnly: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const pos = dp.visualPosition!;
  const hasDetails = dp.details && dp.details.length > 0;

  // Edge-aware anchoring so labels don't get clipped at the diagram edges.
  const tx = pos.x < 18 ? "translateX(0)" : pos.x > 82 ? "translateX(-100%)" : "translateX(-50%)";
  const ty = pos.y < 10 ? "translateY(0)" : pos.y > 90 ? "translateY(-100%)" : "translateY(-50%)";

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          onClick={() => !readOnly && onToggle(dp.key)}
          disabled={readOnly}
          className={`group/pin absolute z-10 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium shadow-sm border transition-all ${
            selected
              ? "bg-background text-foreground border-primary shadow-md ring-2 ring-primary/30"
              : "bg-background text-muted-foreground border-primary/40 ring-2 ring-primary/15 hover:border-primary hover:ring-primary/40 hover:text-foreground"
          } ${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110 hover:shadow-lg hover:z-20"}`}
          style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: `${tx} ${ty}` }}
        >
          {dp.nonSpatial ? (
            <FileText className="h-3 w-3 text-muted-foreground" />
          ) : (
            <JurisdictionBadge level={dp.jurisdiction} />
          )}
          <span className="whitespace-nowrap">{dp.label}</span>
          <span className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary/15 text-primary text-[9px] font-bold leading-none group-hover/pin:bg-primary group-hover/pin:text-primary-foreground transition-colors">
            i
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 p-0" side="top" align="center">
        <div className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <JurisdictionBadge level={dp.jurisdiction} showLabel />
              {dp.nonSpatial && (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Document</span>
              )}
            </div>
            {!readOnly && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <Checkbox
                  checked={selected}
                  onCheckedChange={() => onToggle(dp.key)}
                  className="h-3.5 w-3.5"
                />
                {selected ? "Included" : "Add"}
              </label>
            )}
          </div>
          <div className="text-sm font-semibold leading-tight">{dp.label}</div>
          {dp.summary && (
            <p className="text-sm text-foreground/90 leading-snug">
              <span className="text-muted-foreground italic mr-1">ex.</span>
              {dp.summary}
            </p>
          )}
          {hasDetails && (
            <Collapsible open={showAll} onOpenChange={setShowAll}>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? "" : "-rotate-90"}`} />
                {showAll ? "Hide examples" : `View all ${dp.details!.length} example specs`}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <ul className="space-y-1.5 text-xs text-foreground/80 list-disc pl-4">
                  {dp.details!.map((d, i) => (
                    <li key={i} className="leading-snug">{d}</li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
